import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Box,
    Stack
} from '@mui/material';
import { Helmet } from 'react-helmet-async';


const Unsubscribe = () => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [errorMessage, setErrorMessage] = useState('');

    // Extract the email query parameter on component load
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const emailParam = params.get('email');
        if (emailParam) {
            setEmail(emailParam);
        } else {
            setStatus('error');
            setErrorMessage('No email address provided in the link.');
        }
    }, []);

    // Handle the unsubscribe confirmation click
    const handleConfirmUnsubscribe = async () => {
        if (!email) return;

        setStatus('loading');
        setErrorMessage('');

        try {
            const response = await fetch(`https://nosk.billfern.ca/api/unsubscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setStatus('success');
            } else {
                setStatus('error');
                setErrorMessage(data.message || 'Failed to unsubscribe. Please try again later.');
            }
        } catch (error) {
            console.error('Network error during unsubscribe:', error);
            setStatus('error');
            setErrorMessage('Network error. Please check your connection and try again.');
        }
    };

    return (
      <>
      <Helmet>
        <title>Unsubscribe - Nostalgia Kitchen</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
        <Container 
            maxWidth="sm" 
            sx={{ 
                minHeight: '80vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: 4
            }}
        >
            <Paper 
                elevation={3} 
                sx={{ 
                    p: { xs: 3, sm: 4 }, 
                    width: '100%', 
                    textAlign: 'center',
                    borderRadius: 2 
                }}
            >
                <Typography variant="h4" component="h2" gutterBottom fontWeight="medium">
                    Mailing List Unsubscribe
                </Typography>

                {status === 'error' && errorMessage && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {errorMessage}
                    </Alert>
                )}

                {status === 'success' ? (
                    <Box>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            <strong>{email}</strong> has been successfully removed from our mailing list.
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                            You will no longer receive updates from Nostalgia Kitchen.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ mt: 2 }}>
                        {email ? (
                            <Stack spacing={3} alignItems="center">
                                <Typography variant="body1" color="text.secondary">
                                    Are you sure you want to unsubscribe <strong>{email}</strong> from the Nostalgia Kitchen mailing list?
                                </Typography>

                                <Button
                                    variant="contained"
                                    color="error"
                                    size="large"
                                    fullWidth
                                    onClick={handleConfirmUnsubscribe}
                                    disabled={status === 'loading'}
                                    sx={{ py: 1.5 }}
                                >
                                    {status === 'loading' ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CircularProgress size={22} color="inherit" />
                                            <span>Processing...</span>
                                        </Box>
                                    ) : (
                                        'Confirm Unsubscribe'
                                    )}
                                </Button>
                            </Stack>
                        ) : (
                            <Typography variant="body1" color="error">
                                Invalid or missing unsubscribe link.
                            </Typography>
                        )}
                    </Box>
                )}
            </Paper>
        </Container>
      </>
    );
};

export default Unsubscribe;